/**
 * MarkdownMessage — renders TERRA responses using react-markdown.
 * Supports GFM tables, syntax-highlighted code blocks with copy,
 * and clean dark-theme typography with good visual hierarchy.
 */
import { useMemo, useState, useCallback, createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { CitationBadge } from './CitationBadge';
import type { Citation } from '@/api/terra';

// Context to pass citations into markdown components
const CitationsContext = createContext<Citation[]>([]);

// Module-level plugin arrays to avoid re-creation on every render
const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [rehypeHighlight, rehypeKatex];

/** Copy-to-clipboard button for code blocks */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-mono-700/60 hover:bg-mono-600/60 text-mono-300 hover:text-mono-100"
      title="Copy code"
    >
      {copied ? (
        <CheckIcon className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

/** Extract plain text from code block children */
function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractText((children as any).props.children);
  }
  return String(children ?? '');
}

/** Process text children to replace [N] citation refs with CitationBadge */
function CitationText({ children }: { children: React.ReactNode }) {
  const citations = useContext(CitationsContext);
  if (!citations.length) return <>{children}</>;

  // Only process string children
  if (typeof children !== 'string') return <>{children}</>;

  const parts: React.ReactNode[] = [];
  const regex = /\[(\d+)\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(children)) !== null) {
    if (match.index > lastIndex) {
      parts.push(children.slice(lastIndex, match.index));
    }
    const idx = parseInt(match[1], 10);
    const citation = citations.find((c) => c.index === idx);
    if (citation) {
      parts.push(<CitationBadge key={match.index} index={idx} label={citation.label} />);
    } else {
      parts.push(match[0]); // Keep as-is if no matching citation
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  return <>{parts}</>;
}

interface Props {
  content: string;
  citations?: Citation[];
}

export function MarkdownMessage({ content, citations = [] }: Props) {
  const components = useMemo(
    () => ({
      // Headings — distinct from bold text with size + accent border
      h1: ({ children, ...props }: any) => (
        <h2 className="text-[15px] font-semibold text-cyan-300 mt-5 mb-2 pb-1 border-b border-mono-700/40" {...props}>
          {children}
        </h2>
      ),
      h2: ({ children, ...props }: any) => (
        <h3 className="text-[13px] font-semibold text-cyan-300 mt-4 mb-2" {...props}>
          {children}
        </h3>
      ),
      h3: ({ children, ...props }: any) => (
        <h4 className="text-[13px] font-medium text-mono-200 mt-3 mb-1.5" {...props}>
          {children}
        </h4>
      ),

      // Inline formatting — bold is medium weight, not heavy
      strong: ({ children, ...props }: any) => (
        <strong className="font-medium text-mono-50" {...props}>{children}</strong>
      ),
      em: ({ children, ...props }: any) => (
        <em className="text-mono-300 italic" {...props}>{children}</em>
      ),

      // Links
      a: ({ children, href, ...props }: any) => (
        <a
          href={href}
          className="text-cyan-400 hover:text-cyan-300 underline"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      ),

      // Lists — more breathing room between items
      ul: ({ children, ...props }: any) => (
        <ul className="list-disc list-outside ml-4 space-y-1.5 my-2" {...props}>{children}</ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol className="list-decimal list-outside ml-4 space-y-1.5 my-2" {...props}>{children}</ol>
      ),
      li: ({ children, ...props }: any) => (
        <li className="text-mono-200 leading-relaxed pl-1" {...props}>
          {Array.isArray(children)
            ? children.map((child: React.ReactNode, i: number) =>
                typeof child === 'string' ? <CitationText key={i}>{child}</CitationText> : child
              )
            : typeof children === 'string' ? <CitationText>{children}</CitationText> : children
          }
        </li>
      ),

      // Paragraphs — more vertical rhythm, with inline citation processing
      p: ({ children, ...props }: any) => (
        <p className="my-2 leading-relaxed text-mono-200" {...props}>
          {Array.isArray(children)
            ? children.map((child: React.ReactNode, i: number) =>
                typeof child === 'string' ? <CitationText key={i}>{child}</CitationText> : child
              )
            : typeof children === 'string' ? <CitationText>{children}</CitationText> : children
          }
        </p>
      ),

      // Code blocks
      pre: ({ children, ...props }: any) => {
        const codeText = extractText(children);
        return (
          <div className="relative group my-3">
            <CopyButton text={codeText} />
            <pre className="p-3 bg-mono-900/60 rounded-lg text-xs font-mono overflow-x-auto text-mono-200" {...props}>
              {children}
            </pre>
          </div>
        );
      },

      // Inline code
      code: ({ children, className, ...props }: any) => {
        if (className) {
          return <code className={className} {...props}>{children}</code>;
        }
        return (
          <code className="px-1 py-0.5 bg-mono-800/60 text-cyan-300 rounded text-xs font-mono" {...props}>
            {children}
          </code>
        );
      },

      // Tables
      table: ({ children, ...props }: any) => (
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-xs border border-mono-700/50" {...props}>
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }: any) => (
        <thead className="bg-mono-800/60" {...props}>{children}</thead>
      ),
      th: ({ children, ...props }: any) => (
        <th className="px-2 py-1.5 text-left text-xs font-medium text-mono-300 border-b border-mono-700/50" {...props}>
          {children}
        </th>
      ),
      tr: ({ children, ...props }: any) => (
        <tr className="even:bg-mono-800/20" {...props}>{children}</tr>
      ),
      td: ({ children, ...props }: any) => (
        <td className="px-2 py-1 text-xs text-mono-200 border-b border-mono-800/50" {...props}>
          {children}
        </td>
      ),

      // Horizontal rule
      hr: (props: any) => (
        <hr className="border-mono-700/50 my-4" {...props} />
      ),

      // Blockquote
      blockquote: ({ children, ...props }: any) => (
        <blockquote className="border-l-2 border-cyan-500/30 pl-3 my-3 text-mono-300 italic" {...props}>
          {children}
        </blockquote>
      ),
    }),
    [],
  );

  return (
    <CitationsContext.Provider value={citations}>
      <div className="space-y-1 text-sm text-mono-100">
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={components}
        >
          {content}
        </ReactMarkdown>
      </div>
    </CitationsContext.Provider>
  );
}
